import { useCallback, useEffect, useRef } from 'react';
import type { CSSProperties, KeyboardEvent } from 'react';

// A one-line text field (a title, a to-do label) that wraps and grows instead
// of scrolling sideways, so a long text stays readable while it's typed. It
// never holds a line break: Enter is left to the caller (validate), and a
// pasted line break becomes a space.
export function LineTextarea({ value, onChange, onKeyDown, onBlur, placeholder, autoFocus, className, style }: {
  value: string;
  onChange: (v: string) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  onBlur?: () => void;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
  style?: CSSProperties;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const resize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }, []);
  useEffect(() => { resize(); }, [value, resize]);

  return (
    <textarea
      ref={ref}
      rows={1}
      className={`line-textarea ${className ?? 'form-input--bare'}`}
      style={style}
      value={value}
      placeholder={placeholder}
      autoFocus={autoFocus}
      onChange={(e) => onChange(e.target.value.replace(/\s*[\r\n]+\s*/g, ' '))}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.preventDefault();
        onKeyDown?.(e);
      }}
      onBlur={onBlur}
    />
  );
}
